import { Down } from "@icon-park/svg";
import { Icon, Title } from "@justhome/react-ui";
import clsx from "clsx";
import { FC, PropsWithChildren, useCallback, useId, useState } from "react";
import AnimateHeight from "react-animate-height";
import { SimpleAccordionContrast } from "./types";

type SimpleAccordionSize = "lg" | "md" | "sm";

export interface SimpleAccordionProps extends PropsWithChildren {
  title: string;
  initiallyOpen?: boolean;
  size?: SimpleAccordionSize;
  inverted?: boolean;
  last?: boolean;
}

type VariantClassNames = {
  buttonClassName: string;
  iconClassName: string;
};

const paddingClassName = "px-none m:px-none";

const variants: Record<SimpleAccordionContrast, VariantClassNames> = {
  default: {
    buttonClassName: "hover:text-primary-825",
    iconClassName: "text-primary-800",
  },
  inverted: {
    buttonClassName: "hover:text-neutral-50",
    iconClassName: "text-neutral-0",
  },
};

const SimpleAccordion: FC<SimpleAccordionProps> = ({
  title,
  children,
  initiallyOpen = false,
  size = "md",
  inverted = false,
  last = false,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const id = useId();
  const panelId = useId();

  const handleClick = useCallback(() => {
    setIsOpen((o) => !o);
  }, []);

  const { buttonClassName, iconClassName } =
    variants[inverted ? "inverted" : "default"];

  return (
    <div
      className={(last ? "border-y" : "border-t") + " border-neutral-50"}
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <h3>
        <button
          type="button"
          itemProp="name"
          aria-expanded={isOpen}
          aria-controls={panelId}
          id={id}
          tabIndex={0}
          onClick={handleClick}
          className={clsx(
            "focusable shadow-primary-800 focus:outline-none flex justify-between w-full items-center rounded-s text-left",
            {
              "px-3s m:px-m py-3s text-h3": size === "lg",
              "px-3s m:px-2s py-2s text-body-1": size === "md",
              "p-s m:px-m py-s text-app-h3": size === "sm",
            },
            paddingClassName,
            buttonClassName,
          )}
        >
          <Title className="mr-2xs" as="span">
            {title}
          </Title>
          <Icon
            icon={Down}
            size={size === "sm" ? 20 : 24}
            className={clsx(iconClassName, {
              "rotate-180": isOpen,
            })}
          />
        </button>
      </h3>
      <AnimateHeight height={isOpen ? "auto" : 0} duration={200}>
        <div
          itemScope
          itemProp="acceptedAnswer"
          itemType="https://schema.org/Answer"
          id={panelId}
          role="region"
          aria-labelledby={id}
          className={clsx(
            "leading-none",
            {
              "px-3s m:px-m pb-3s m:pb-m text-body-2": size === "lg",
              "px-3s m:px-2s pb-3s m:pb-m text-body-2": size === "md",
              "px-s pb-s m:px-m text-app-body-2": size === "sm",
            },
            paddingClassName,
          )}
        >
          <div itemProp="text">{children}</div>
        </div>
      </AnimateHeight>
    </div>
  );
};

export default SimpleAccordion;
